import { create } from "zustand";
import { devtools, persist, createJSONStorage } from "zustand/middleware";
import { useShallow } from "zustand/react/shallow";
import { createAuthSlice, auth } from "@core/features/auth";
import {
  createAppSlice,
  type BaseAppStore,
  defaultFeatureFlags,
} from "@core/features/appStore";
import {
  createNotificationsSlice,
  notifications,
  type NotificationsSlice,
} from "@core/features/notifications";
import {
  createSupportSlice,
  support,
  type SupportSlice,
} from "@core/features/support";
import { createChatSlice, chat, type ChatSlice } from "@core/features/chat";
import {
  createProgramSlice,
  program,
  type ProgramSlice,
} from "@core/features/program";
import {
  createInvitationSlice,
  invitation,
  type InvitationSlice,
} from "@core/features/invitation";
import {
  createDialogSlice,
  dialog,
  type DialogSlice,
} from "@core/features/dialog";
import {
  createMobileMenuSlice,
  mobileMenu,
  type MobileMenuSlice,
} from "@core/features/mobileMenu";
import {
  createOnboardingSlice,
  onboarding,
  type OnboardingSlice,
} from "@core/features/onboarding";
import {
  createMarketingSlice,
  marketing,
  type MarketingSlice,
} from "@core/features/marketing";
import {
  createPublicProfileSlice,
  publicProfile,
  type PublicProfileSlice,
} from "@core/features/publicProfile";
import {
  createVideoMeetingSlice,
  videoMeeting,
  type VideoMeetingSlice,
} from "@core/features/videoMeeting";
import {
  createVideoSlice,
  video,
  type VideoSlice,
} from "@core/features/video";
import {
  createVideoBackgroundsSlice,
  videoBackgrounds,
  type VideoBackgroundsSlice,
} from "@core/features/videoBackgrounds";


// ============================================================================
// Combined Platform Store
// ============================================================================

/**
 * Full platform store type combining all slices.
 */
export type PlatformStore = BaseAppStore &
  NotificationsSlice &
  SupportSlice &
  ChatSlice &
  ProgramSlice &
  InvitationSlice &
  DialogSlice &
  MobileMenuSlice &
  OnboardingSlice &
  MarketingSlice &
  PublicProfileSlice &
  VideoMeetingSlice &
  VideoSlice &
  VideoBackgroundsSlice;

/**
 * Platform feature flags.
 */
const platformFeatureFlags = {
  ...defaultFeatureFlags,
  isSignupDisabled: false,
  isChatDisabled: false,
  isAddToCalendarDisabled: false,
  isWorkbookDownloadDisabled: false,
  isTranscriptionDisabled: false,
  isSupportPageDisabled: true,
  isVideoDisabled: true,
  isEnrollmentClosed: true,
};

/**
 * The platform store.
 * Combines base app slices with notifications pagination and platform-specific slices.
 *
 * NOTE: Server data (notifications list, unread count) lives in React Query.
 * Zustand only holds client-only state (pagination, UI state).
 * See architecture.mdc for the "React Query as Source of Truth" pattern.
 *
 * @example
 * ```typescript
 * // Get state
 * const user = usePlatformStore((s) => s.user);
 * const page = usePlatformStore((s) => s.notificationsPage);
 *
 * // Get actions
 * const setUser = usePlatformStore((s) => s.setUser);
 * const setPage = usePlatformStore((s) => s.setNotificationsPage);
 *
 * // Use React Query for notification data
 * const { data, isLoading } = useFetchNotifications({ user, page });
 * ```
 */
export const usePlatformStore = create<PlatformStore>()(
  devtools(
    persist(
      (set, get, store) => ({
        // Base slices from core
        ...createAuthSlice(set as never, get as never, store as never),
        ...createAppSlice(set as never, get as never, store as never),

        // Override feature flags
        featureFlags: platformFeatureFlags,

        // Notifications slice from core
        ...createNotificationsSlice(set as never, get as never, store as never),

        // Support slice from core
        ...createSupportSlice(set as never, get as never, store as never),

        // Chat slice from core
        ...createChatSlice(set as never, get as never, store as never),

        // Program slice from core
        ...createProgramSlice(set as never, get as never, store as never),

        // Invitation slice from core
        ...createInvitationSlice(set as never, get as never, store as never),

        // Dialog slice from core
        ...createDialogSlice(set as never, get as never, store as never),

        // Mobile menu slice from core
        ...createMobileMenuSlice(set as never, get as never, store as never),

        // Onboarding slice from core
        ...createOnboardingSlice(set as never, get as never, store as never),

        // Marketing slice from core
        ...createMarketingSlice(set as never, get as never, store as never),

        // Public profile slice from core
        ...createPublicProfileSlice(set as never, get as never, store as never),

        // Video slices from core
        ...createVideoMeetingSlice(set as never, get as never, store as never),
        ...createVideoSlice(set as never, get as never, store as never),
        ...createVideoBackgroundsSlice(set as never, get as never, store as never),

      }),
      {
        name: "polpltd",
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({
          user: state.user,
          authToken: state.authToken,
          // The dashboard chat surface lets multi-circle users pick which
          // circle's conversation to view. Persisting the choice keeps the
          // selection across reloads; keying it by programId ensures a
          // user in multiple programs doesn't see program A's selection
          // leak into program B.
          selectedCircleByProgram: state.selectedCircleByProgram,
        }),
        onRehydrateStorage: () => (state) => {
          console.log("Rehydrated state from localStorage:", state?.user);
          state?.setHydrated(true);
        },
      }
    ),
    {
      name: "PlatformStore",
      enabled: process.env.NODE_ENV === "development",
    }
  )
);

// ============================================================================
// Selector hooks for common patterns
// ============================================================================

/** Get current user */
export const useUser = () => usePlatformStore((s) => s.user);

/** Check if user is authenticated */
export const useIsAuthenticated = () => usePlatformStore((s) => !!s.user);

/** Get invitation state and actions */
export const useInvitation = () =>
  usePlatformStore(useShallow(invitation.state));

/** Get feature flags (shallow comparison to prevent unnecessary re-renders) */
export const useFeatureFlags = () =>
  usePlatformStore(useShallow((s) => s.featureFlags));

/** Check if a specific feature is enabled */
export const useIsFeatureEnabled = (flag: keyof typeof platformFeatureFlags) =>
  usePlatformStore((s) => !s.featureFlags[flag]);

/** Get notifications pagination state */
export const useNotificationsPagination = () =>
  usePlatformStore(useShallow(notifications.pagination));

/** Get auth state and actions */
export const useAuth = () => usePlatformStore(useShallow(auth.state));

/** Get support sheet state and actions */
export const useSupportSheet = () =>
  usePlatformStore(useShallow(support.sheet));

/** Get chat sheet state and actions */
export const useChatSheet = () => usePlatformStore(useShallow(chat.sheet));

/** Get chat circle-selection state and actions (per-program persistence) */
export const useChatSelection = () =>
  usePlatformStore(useShallow(chat.selection));

/**
 * Get the chat-session retry counter and bump action.
 *
 * `key` is the counter that flows through to `<ChatboxStuckDetector>`'s
 * `resetSignal` and `<ChatSession>`'s `syncUser` memo dep. `bump` is wired
 * to the Try Again button on `<ChatErrorState>`. Bumping triggers an
 * in-place TalkJS Session recycle without remounting `<Session>` or its
 * children. See docs/chat-stuck-spinner-fix-plan.md §5.4.
 */
export const useChatSessionRetry = () =>
  usePlatformStore(useShallow(chat.sessionRetry));

/** Get selected phase state and actions */
export const useSelectedPhase = () =>
  usePlatformStore(useShallow(program.selectedPhase));

/** Get dialog state and actions */
export const useDialog = () => usePlatformStore(useShallow(dialog.state));

/** Get mobile menu state and actions */
export const useMobileMenu = () =>
  usePlatformStore(useShallow(mobileMenu.state));

/** Get onboarding state and actions */
export const useOnboarding = () =>
  usePlatformStore(useShallow(onboarding.state));

/** Get current onboarding step for a program */
export const useOnboardingCurrentStep = (programId: number) =>
  usePlatformStore(onboarding.currentStep(programId));

/** Get skipped steps for a program */
export const useOnboardingSkippedSteps = (programId: number) =>
  usePlatformStore(onboarding.skippedSteps(programId));

/** Get marketing mode state and actions */
export const useMarketingMode = () =>
  usePlatformStore(useShallow(marketing.state));

/** Get computed marketing mode selectors */
export const useMarketingComputed = () =>
  usePlatformStore(useShallow(marketing.computed));

/** Get public profile sheet state and actions */
export const usePublicProfileSheet = () =>
  usePlatformStore(useShallow(publicProfile.sheet));

// ============================================================================
// Video selector hooks
// ============================================================================

/** Get video meeting details state */
export const useVideoMeetingDetails = () =>
  usePlatformStore(useShallow(videoMeeting.details));

/** Get video meeting access state */
export const useVideoMeetingAccess = () =>
  usePlatformStore(useShallow(videoMeeting.access));

/** Get video meeting members state */
export const useVideoMeetingMembers = () =>
  usePlatformStore(useShallow(videoMeeting.members));

/** Get video meeting transcription state */
export const useVideoMeetingTranscription = () =>
  usePlatformStore(useShallow(videoMeeting.transcription));

/** Get video roster (Chime SDK attendees synced to Zustand) */
export const useVideoRoster = () => usePlatformStore((s) => s.roster);

/** Get video session state */
export const useVideoSession = () =>
  usePlatformStore(useShallow(video.session));

/** Get video config state */
export const useVideoConfig = () =>
  usePlatformStore(useShallow(video.config));

/** Get video UI state (sidebar, tabs) */
export const useVideoUI = () => usePlatformStore(useShallow(video.ui));

/** Get materials-tab article navigation state (inline back-stack of slugs). */
export const useVideoMaterialsNavigation = () =>
  usePlatformStore(useShallow(video.materialsNavigation));

/** Get video zoom state */
export const useVideoZoom = () => usePlatformStore(useShallow(video.zoom));

/** Get selected video device IDs */
export const useVideoDevices = () =>
  usePlatformStore(useShallow(video.devices));

/** Get video tile grid state (attendee states, video source state) */
export const useVideoTileGrid = () =>
  usePlatformStore(useShallow(video.tileGrid));

/** Get video reactions state and actions (ephemeral emoji reactions per attendee) */
export const useVideoReactions = () =>
  usePlatformStore(useShallow(video.reactions));

/** Get video backgrounds state */
export const useVideoBackgrounds = () =>
  usePlatformStore(useShallow(videoBackgrounds.all));
